import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getAlbumMedia, isNoAccessError } from "./api";
import type { MediaEntry } from "./types";

/** Loads one album's media list, shared by DualcamAlbumPage and DualcamMapPage (previously each
 * had its own copy-pasted load()/notFound handling). `notFound` is distinct from "loaded and
 * genuinely empty" — true when the album was deleted, revoked, or never belonged to this user, so
 * callers can show a real "no access" message instead of the misleading "nothing here yet" empty
 * state a plain empty array would imply. */
export function useAlbumMedia(albumId: string | undefined, includeGps = false) {
    const { user } = useAuth();
    const [media, setMedia] = useState<MediaEntry[] | null>(null);
    // Whether this album accepts uploads, and whether the caller owns it (vs. a friend's shared copy) —
    // same signals Album.canUpload/Album.shared report, carried on the media response itself (see
    // AlbumMedia) rather than needing a separate listAlbums call to learn them.
    const [canUpload, setCanUpload] = useState(false);
    const [isOwner, setIsOwner] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<{ kind: "timeout" | "error"; message: string } | null>(null);
    const timeoutId = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const load = useCallback(async () => {
        if (!user || !albumId) return;
        setNotFound(false);
        setLoadError(null);
        setLoading(true);
        // Reset up front, not only in the no-access branch below: otherwise a load that fails with a
        // plain error (network/NAS, not "no access") would leave whatever album this last successfully
        // loaded still marked canUpload/isOwner — showing this new, still-failed album's page with a
        // stale Upload/Credit-to control from a previous one.
        setCanUpload(false);
        setIsOwner(false);
        if (timeoutId.current !== undefined) clearTimeout(timeoutId.current);
        timeoutId.current = undefined;
        let timeout: ReturnType<typeof setTimeout> | undefined;
        let timedOut = false;
        try {
            const result = await Promise.race([
                getAlbumMedia(user.token, Number(albumId), includeGps),
                new Promise<never>((_, reject) => {
                    timeout = setTimeout(() => {
                        timedOut = true;
                        reject(new Error(
                        navigator.language.toLowerCase().startsWith("de")
                            ? "Das Album antwortet nicht. Prüfe, ob das NAS erreichbar ist, und versuche es erneut."
                            : "The album is taking too long to respond. Check that the NAS is reachable, then try again."
                        ));
                    }, 15_000);
                    timeoutId.current = timeout;
                }),
            ]);
            setMedia(result.items);
            setCanUpload(result.canUpload);
            setIsOwner(result.isOwner);
        } catch (err) {
            if (isNoAccessError(err)) {
                setNotFound(true);
                setMedia([]);
            } else {
                const message = (err as Error).message;
                setLoadError({ kind: timedOut ? "timeout" : "error", message });
            }
        } finally {
            if (timeout !== undefined) clearTimeout(timeout);
            if (timeoutId.current === timeout) timeoutId.current = undefined;
            setLoading(false);
        }
    }, [user, albumId, includeGps]);

    // Keyed on the token rather than the whole user object: AuthProvider swaps that object once after
    // validateSession, and depending on it would fire a second full NAS listing for every album open.
    // The token still has to be here — on a cold load the first render has no user yet, load() returns
    // immediately, and without re-running once it arrives the page would wait for a fetch that never
    // happened.
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [albumId, user?.token]);

    useEffect(() => () => {
        if (timeoutId.current !== undefined) clearTimeout(timeoutId.current);
    }, []);

    // `loading` is real state, not `media === null`. Derived that way, a transient NAS or network
    // failure — which never assigns media — left the page spinning for ever behind a toast that had
    // already faded, with a manual refresh the only way out. `loadError` gives callers something to
    // show and `reload` something to retry with; it is also what a page that *changes* the album's
    // contents (uploading) uses to refresh without duplicating this hook's handling.
    return { media, canUpload, isOwner, loading, notFound, loadError, reload: load };
}
